import { ThemeService } from '@core/services/theme.service';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  NgZone,
  inject,
  PLATFORM_ID,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AlertModalComponent } from '../shared/alert-modal/alert-modal.component';
import { TenableApiService } from '@core/tenable/service/tenable-api.service';
import { UserService } from '@core/services/user.service';
import { User } from '@core/models/user';
import { Company } from '@core/models/company';
import { CompanyService } from '@core/services/company.service';
import { isChannelPartnerCompany } from '@core/utils/channel-partner';
import { NinjaScannerService } from '@core/services/ninja-scanner.service';
import { NinjaAgentApiService } from '@core/services/ninja-agent-api.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoggingService } from '@core/services/logging.service';
import {
  ScanCompletionMonitorService,
  ScanMonitoringStatus,
} from '@core/services/scan-completion-monitor.service';
import { UserActivityLoggingService } from '@core/services/user-activity-logging.service';
import { Subscription, interval, Subject, forkJoin, of } from 'rxjs';
import { takeUntil, switchMap, map, catchError, filter } from 'rxjs/operators';
import { SafeStorageService } from '@core/services/safe-storage.service';
import { NinjaCreditsService } from '@core/services/ninja-credits.service';
import { RuntimeConfigurationService } from '@core/services/runtime-configuration.service';
import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';
import { NavigationEnd, Router } from '@angular/router';
import { AuthFlowService } from '@core/services/auth-flow.service';
import { multiFactor } from 'firebase/auth';

/**
 * AdminLayoutComponent
 *
 * Top-level layout for the admin dashboard. Responsibilities:
 * - Subscribes to global spinner visibility for theme loader overlay
 * - Initializes and monitors scan completion service
 * - Loads user/company context for dashboard rendering
 * - Preloads sidebar-related counts (scanners, assets, scans)
 * - Initializes user activity logging
 */
@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
  styleUrl: './admin-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  private tenableApiService = inject(TenableApiService);
  private ninjaScannerService = inject(NinjaScannerService);
  private ninjaAgentApiService = inject(NinjaAgentApiService);
  private themeService = inject(ThemeService);
  private userSvc = inject(UserService);
  private companySvc = inject(CompanyService);
  private companyService = inject(CompanyService);

  /**
   * Channel-partner company (#943): the low-Coins banner keeps its warning
   * but drops the Top Up button, which would lead to prices.
   */
  get isPartnerCompany(): boolean {
    return isChannelPartnerCompany(this.companyService.company);
  }
  private cdRef = inject(ChangeDetectorRef);
  private modalService = inject(NgbModal);
  private loggingService = inject(LoggingService);
  private scanCompletionMonitor = inject(ScanCompletionMonitorService);
  private activityLogging = inject(UserActivityLoggingService);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private safeStorage = inject(SafeStorageService);
  private ninjaCreditsService = inject(NinjaCreditsService);
  private runtimeConfiguration = inject(RuntimeConfigurationService);
  private firebaseHelper = inject(FirebaseInjectionHelperService);
  private router = inject(Router);
  private authFlowService = inject(AuthFlowService);

  /** Low-credit alert: observable for the global banner */
  readonly creditSummary$ = this.ninjaCreditsService.creditSummary$;

  /** MFA prompt banner: true when policy is 'prompt' and no factor enrolled. */
  public showMfaPromptBanner = false;
  /** Session-scoped dismissal key for the MFA prompt banner. */
  private readonly mfaBannerDismissKey = 'mfaPromptBannerDismissed';

  public showSideBar: boolean = true;
  public spinnerShow: boolean = true;
  public isSidebarCollapsed: boolean = false;

  @ViewChild('alertModal') alertModal!: AlertModalComponent;

  userModel!: User;
  companyModel!: Company;

  // Error recovery state
  public hasRecoverableError = false;
  public errorDetails?: string;
  private retryAttempts = 0;
  private readonly MAX_RETRY_ATTEMPTS = 3;

  // Scan completion monitoring properties
  private monitoringStatusSubscription?: Subscription;
  private destroy$ = new Subject<void>();

  /**
   * Injects required services and triggers initial user/company loading.
   */
  constructor() {
    this.getUserCompany();
  }

  /**
   * Lifecycle hook: initializes spinner subscription, error modal wiring,
   * scan monitoring, logging, and preloads sidebar counts.
   */
  ngOnInit(): void {
    // Initialize user activity logging for dashboard access
    this.activityLogging.logPageView('admin-dashboard', { component: 'admin-layout' });

    // Keep the post-login return URL pointing at the page the user is actually
    // on. adminAuthGuard only records the URL when the dashboard parent route
    // activates, so without this a refresh that detoured through /login always
    // came back to the first dashboard page of the session (#730).
    this.rememberDashboardUrl(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.rememberDashboardUrl(event.urlAfterRedirects);
      });

    this.themeService
      .getShowSpinner()
      .pipe(takeUntil(this.destroy$))
      .subscribe((showSpinner) => {
        this.spinnerShow = showSpinner;
        this.cdRef.detectChanges(); // Manually trigger change detection
      });

    this.themeService.currentErrorMessage
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        if (message) {
          const modalRef = this.modalService.open(AlertModalComponent);
          modalRef.componentInstance.message = message;
          modalRef.componentInstance.showYesNoButtons = false;
        }
      });

    // Initialize the scan completion monitoring system
    this.initializeScanCompletionMonitoring();

    // Start the 1-minute monitoring status logger
    this.startMonitoringStatusLogger();

    // Preload application-level data (counts, flags) early to reduce duplicate API calls
    this.preloadApplicationData();

    // Listen for sidebar collapsed events (browser only)
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('sidebar:collapsed', (e: any) => {
        try {
          this.isSidebarCollapsed = !!e?.detail?.collapsed;
          this.cdRef.detectChanges();
        } catch {
          /* no-op */
        }
      });

      // MFA grace-mode banner (config 'prompt' + no enrolled second factor)
      void this.evaluateMfaPromptBanner();
    }
  }

  /**
   * Records a dashboard route as the post-login return target. Only dashboard
   * URLs qualify: the shell can observe transitional URLs (for example `/`
   * before the first navigation settles) and those must not replace a good
   * value.
   * @param url Router URL to remember.
   */
  private rememberDashboardUrl(url: string): void {
    if (url.startsWith('/dashboard')) {
      this.authFlowService.rememberRedirectUrl(url);
    }
  }

  /**
   * Shows the dismissible MFA enrollment banner when the runtime security
   * policy is 'prompt' and the signed-in user has no enrolled second factor.
   * Dismissal is session-scoped (sessionStorage), not persistent. Best-effort:
   * any failure simply leaves the banner hidden.
   */
  private async evaluateMfaPromptBanner(): Promise<void> {
    try {
      if (sessionStorage.getItem(this.mfaBannerDismissKey) === 'true') {
        return;
      }

      await this.runtimeConfiguration.waitForConfiguration();
      if (this.runtimeConfiguration.getMfaEnforcement() !== 'prompt') {
        return;
      }

      const firebaseUser = this.firebaseHelper.getAuth().currentUser;
      if (
        !firebaseUser ||
        multiFactor(firebaseUser).enrolledFactors.length > 0
      ) {
        return;
      }

      this.showMfaPromptBanner = true;
      this.cdRef.detectChanges();
    } catch (error) {
      this.loggingService.logDebug('MFA prompt banner check skipped', {
        error,
      });
    }
  }

  /**
   * Hides the MFA prompt banner for the rest of the browser session.
   */
  public dismissMfaPromptBanner(): void {
    this.showMfaPromptBanner = false;
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(this.mfaBannerDismissKey, 'true');
    }
  }

  /**
   * Lifecycle hook: tear down subscriptions and stop monitoring.
   */
  ngOnDestroy(): void {
    // Signal to all subscriptions to complete
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up any remaining subscriptions
    if (this.monitoringStatusSubscription) {
      this.monitoringStatusSubscription.unsubscribe();
    }

    this.scanCompletionMonitor.stopMonitoring();
  }

  /**
   * Loads the current user and company context.
   */
  getUserCompany() {
    this.userSvc
      .getUserModel()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user && user.uid) {
          this.userModel = user;

          if (!this.userModel.currentCompanyId) {
            this.loggingService.logDebug('getUserCompany: user has no currentCompanyId, skipping company lookup');
            return;
          }

          this.companySvc
            .getDocument(this.userModel.currentCompanyId)
            .then(async (company: Company) => {
              if (company && company.uid) {
                this.companyModel = company;

                // Track if we need to update the company document
                let needsUpdate = false;

                // Backfill ownerId if missing (data integrity fix)
                if (!this.companyModel.ownerId && this.userModel.uid) {
                  this.loggingService.logDebug('Backfilling missing ownerId on company');
                  this.companyModel.ownerId = this.userModel.uid;
                  needsUpdate = true;
                }

                // Backfill name if missing
                if (!this.companyModel.name && this.userModel.displayName) {
                  this.loggingService.logDebug('Backfilling missing company name');
                  this.companyModel.name = `${this.userModel.displayName}'s Company`;
                  needsUpdate = true;
                }

                // Derive domain from user email if missing
                if (!this.companyModel.domain) {
                  this.companyModel =
                    this.companySvc.updateDomainFromOwnerEmail(
                      this.companyModel,
                      this.userModel,
                    );
                  if (this.companyModel.domain) {
                    needsUpdate = true;
                  }
                }

                // Persist the backfilled data to Firestore
                if (needsUpdate) {
                  try {
                    await this.companySvc.update(this.companyModel, this.companyModel.uid);
                    this.loggingService.logDebug('Company data backfilled successfully');
                  } catch (error) {
                    this.loggingService.logError('Failed to backfill company data', { error });
                  }
                }

                this.retryAttempts = 0;
                this.hasRecoverableError = false;
                this.errorDetails = undefined;
              }
            })
            .catch((error) => {
              this.loggingService.logError('Failed to load company data', { error });
              this.handleRecoverableError('Failed to load company information. Please retry.');
            });
        }
      });
  }

  /**
   * Handles recoverable errors (network, permissions, backend) without forcing logout.
   * Implements retry with exponential backoff. Only logs out on auth invalidation.
   */
  handleRecoverableError(errorMessage: string) {
    this.retryAttempts++;
    this.hasRecoverableError = true;
    this.errorDetails = errorMessage;

    this.loggingService.logError('Recoverable error encountered', {
      message: errorMessage,
      attempt: this.retryAttempts,
      userId: this.userModel?.uid || 'unknown',
      currentCompanyId: this.companyModel?.uid || 'unknown'
    });

    // If we've exhausted retries, show persistent error state
    if (this.retryAttempts >= this.MAX_RETRY_ATTEMPTS) {
      this.loggingService.logError('Max retry attempts reached', {
        message: errorMessage,
        userId: this.userModel?.uid || 'unknown'
      });
      this.themeService.hideSpinner();
      return;
    }

    // Exponential backoff: 2s, 4s, 8s
    const backoffMs = Math.pow(2, this.retryAttempts) * 1000;
    setTimeout(() => {
      this.loggingService.logInfo('Retrying after recoverable error', { attempt: this.retryAttempts });
      this.getUserCompany();
    }, backoffMs);
  }

  /**
   * Manual retry triggered by user action (e.g., clicking a retry button)
   */
  public manualRetry() {
    this.retryAttempts = 0;
    this.hasRecoverableError = false;
    this.errorDetails = undefined;
    this.themeService.showSpinner();
    this.getUserCompany();
  }

  /**
   * Initialize the scan completion monitoring system
   * This automatically starts monitoring since users reaching this component are already authenticated
   * Note: We're using ScanCompletionMonitorService instead of EnhancedScanMonitorService
   * to avoid duplicate polling and hitting Application Insights AJAX limits
   */
  /**
   * Initialize the scan completion monitoring system.
   */
  private initializeScanCompletionMonitoring(): void {
    // Since users are already authenticated when they reach the admin layout,
    // we can start monitoring immediately
    this.scanCompletionMonitor.startMonitoring();
  }

  /**
   * Start a 1-minute interval logger to show monitoring status using Angular-friendly interval
   * This helps debug the monitoring system as requested by user
   */
  /**
   * Start a 1-minute interval logger to show monitoring status using Angular-friendly interval.
   */
  private startMonitoringStatusLogger(): void {
    // Use RxJS interval instead of setInterval for better integration with Angular
    this.monitoringStatusSubscription = interval(60000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.scanCompletionMonitor.getMonitoringStatus()),
      )
      .subscribe((status: ScanMonitoringStatus) => {
        this.ngZone.run(() => {
          // const timestamp = new Date().toLocaleTimeString();
          if (status.errors.length > 0) {
            this.loggingService.logWarning(
              '⚠️ Recent monitoring errors:',
              status.errors.slice(-3),
            );
          }
        });
      });
  }

  /**
   * Preload application-level data (scanners, assets, scans) once and publish
   * counts/flags to ThemeService so dependent components can render quickly
   * without redundant API calls.
   */
  private preloadApplicationData(): void {
    this.companyService
      .getCurrentUserCompany()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((company) => {
          const scanners$ = company?.uid
            ? this.ninjaScannerService.listScanners(company.uid).pipe(catchError(() => of([])))
            : of([]);

          const assets$ = this.tenableApiService.getAssetList().pipe(
            map((resp: any) => resp?.data?.total_count ?? 0),
            catchError(() => of(0)),
          );

          const scans$ = this.ninjaAgentApiService.listScans().pipe(
            map((res) => (Array.isArray(res?.scans) ? res.scans : [])),
            catchError(() => of([])),
          );

          return forkJoin({ scanners: scanners$, assets: assets$, scans: scans$ });
        }),
      )
      .subscribe(({ scanners, assets, scans }) => {
        try {
          this.themeService.setScannersCount(scanners.length);
          this.themeService.setScannerExists(scanners.length > 0);
          this.themeService.setAssetCount(assets as number);
          this.themeService.setScanListCount(scans.length);
        } catch {
          /* no-op */
        }
      });
  }
}
